import { z } from 'zod';

/**
 * Release notes, generated from Conventional Commit subjects between two tags by the release
 * workflow. Bundled into the PWA (apps/web/src/release-notes.json) for the one-time
 * "What's new" card, and rendered as Markdown for the GitHub release.
 */
export const releaseSectionKind = z.enum(['new', 'improved', 'fixed']);
export type ReleaseSectionKind = z.infer<typeof releaseSectionKind>;

export const releaseNotesSchema = z.object({
  /** Without the leading "v"; "dev" in local builds. */
  version: z.string(),
  /** YYYY-MM-DD, null in local builds. */
  date: z.string().nullable(),
  /** Free text from the release workflow input (the annotated tag message). */
  highlights: z.string().nullable(),
  sections: z.array(z.object({ kind: releaseSectionKind, items: z.array(z.string()) })),
});
export type ReleaseNotes = z.infer<typeof releaseNotesSchema>;

export interface Commit {
  subject: string;
  body: string;
}

const USER_FACING: Record<string, ReleaseSectionKind> = {
  feat: 'new',
  perf: 'improved',
  fix: 'fixed',
};
const ORDER: ReleaseSectionKind[] = ['new', 'improved', 'fixed'];

/** `type(scope)!: subject` → parts; null for a subject that is not a Conventional Commit. */
export function parseSubject(
  subject: string,
): { type: string; scope: string | null; text: string } | null {
  const match = /^(\w+)(?:\(([^)]*)\))?!?:\s*(.+)$/.exec(subject.trim());
  if (!match?.[1] || !match[3]) return null;
  return { type: match[1].toLowerCase(), scope: match[2] ?? null, text: match[3].trim() };
}

function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The user-facing part of a release: feat → new, perf → improved, fix → fixed. Everything else
 * (refactor, docs, ci, chore, test, …) lands in `maintenance`, which only GitHub shows.
 */
export function buildReleaseNotes(opts: {
  version: string;
  date: string;
  highlights: string | null;
  commits: Commit[];
}): { notes: ReleaseNotes; maintenance: string[] } {
  const grouped = new Map<ReleaseSectionKind, string[]>();
  const maintenance: string[] = [];
  for (const commit of opts.commits) {
    if (/^Merge /.test(commit.subject)) continue;
    const parsed = parseSubject(commit.subject);
    const kind = parsed ? USER_FACING[parsed.type] : undefined;
    const text = sentence(parsed?.text ?? commit.subject.trim());
    if (!kind) {
      maintenance.push(text);
      continue;
    }
    const items = grouped.get(kind) ?? [];
    if (!items.includes(text)) items.push(text);
    grouped.set(kind, items);
  }
  const highlights = opts.highlights?.trim() ?? '';
  return {
    notes: {
      version: opts.version.replace(/^v/, ''),
      date: opts.date,
      highlights: highlights || null,
      sections: ORDER.flatMap((kind) => {
        const items = grouped.get(kind);
        return items?.length ? [{ kind, items }] : [];
      }),
    },
    maintenance,
  };
}

const HEADINGS: Record<ReleaseSectionKind, string> = {
  new: 'New',
  improved: 'Improved',
  fixed: 'Fixed',
};

/** Body of the GitHub release. */
export function releaseMarkdown(
  notes: ReleaseNotes,
  maintenance: string[],
  compareUrl?: string,
): string {
  const parts: string[] = [];
  if (notes.highlights) parts.push(notes.highlights);
  for (const section of notes.sections) {
    parts.push(`### ${HEADINGS[section.kind]}\n\n${section.items.map((i) => `- ${i}`).join('\n')}`);
  }
  if (maintenance.length > 0) {
    parts.push(`### Maintenance\n\n${maintenance.map((i) => `- ${i}`).join('\n')}`);
  }
  if (parts.length === 0) parts.push('No changes.');
  if (compareUrl) parts.push(`**Full changelog:** ${compareUrl}`);
  return `${parts.join('\n\n')}\n`;
}
